import type { Backend } from "@superego/backend";
import { fromHref } from "@superego/routing";
import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, useLocale } from "react-aria-components";
import { FormattedMessage, IntlProvider } from "react-intl";
import DataLoader from "./business-logic/backend/DataLoader.js";
import { GlobalDataProvider } from "./business-logic/backend/GlobalData.js";
import {
  getDeveloperPromptsQuery,
  getGlobalSettingsQuery,
  listAppsQuery,
  listCollectionCategoriesQuery,
  listCollectionsQuery,
} from "./business-logic/backend/hooks.js";
import { BackendProvider } from "./business-logic/backend/useBackend.js";
import useNavigationState from "./business-logic/navigation/useNavigationState.js";
import ScreenSizeProvider from "./business-logic/screen-size/ScreenSizeProvider.js";
import ResultErrors from "./components/design-system/ResultErrors/ResultErrors.js";
import Root from "./components/routes/Root/Root.js";
import getMessages from "./translations/getMessages.js";
import "./BrowserApp.css.js";

interface Props {
  backend: Backend;
  queryClient: QueryClient;
}
export default function BrowserApp({ backend, queryClient }: Props) {
  const { locale } = useLocale();
  const { navigateTo } = useNavigationState();
  const messages = getMessages(locale);
  return (
    <IntlProvider messages={messages} locale={locale} defaultLocale="en">
      <BackendProvider backend={backend}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider navigate={(href) => navigateTo(fromHref(href))}>
            <ScreenSizeProvider>
              <DataLoader
                queries={[
                  listCollectionCategoriesQuery([]),
                  listCollectionsQuery([false]),
                  listAppsQuery([]),
                  getGlobalSettingsQuery([]),
                  getDeveloperPromptsQuery([]),
                ]}
                renderErrors={(errors) => (
                  <>
                    <h1>
                      <FormattedMessage defaultMessage="Error loading app" />
                    </h1>
                    <ResultErrors errors={errors} highlightDetails={false} />
                  </>
                )}
              >
                {(
                  collectionCategories,
                  collections,
                  apps,
                  globalSettings,
                  developerPrompts,
                ) => (
                  <GlobalDataProvider
                    value={{
                      collectionCategories,
                      collections,
                      apps,
                      globalSettings,
                      developerPrompts,
                    }}
                  >
                    <Root />
                  </GlobalDataProvider>
                )}
              </DataLoader>
            </ScreenSizeProvider>
          </RouterProvider>
        </QueryClientProvider>
      </BackendProvider>
    </IntlProvider>
  );
}
